/**
 * Run dialog: a form generated from the action's input schema, then the run's
 * live result once submitted.
 *
 * @module features/sandboxes/components/v2/detail/RunActionDialog
 */
import React from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  LoadingOrbit,
  NumberField,
  PasswordField,
  Select,
  Text,
  TextField,
} from '@chatsdet/ui';
import { ApiError } from '../../../../../lib/api-error';
import { useRunSandboxV2Action, useSandboxV2Run } from '../../../hooks/useSandboxV2Detail';
import { buildRunInput, initialFormValues, type ActionField, type FormValues, type RunnableAction } from './actionForm';
import { RunResultView } from './RunResultView';

function FieldInput({
  field,
  value,
  personas,
  onChange,
}: {
  field: ActionField;
  value: string | boolean;
  personas: string[];
  onChange: (next: string | boolean) => void;
}) {
  const label = field.required ? `${field.name} *` : field.name;
  if (field.type === 'boolean') {
    return <Checkbox label={field.name} description={field.description} isSelected={value === true} onChange={onChange} />;
  }
  const choices = field.type === 'persona' ? personas : field.options?.map(String);
  if (choices) {
    const items = [
      ...(field.required ? [] : [{ id: '', label: '—' }]),
      ...choices.map((choice) => ({ id: choice, label: choice })),
    ];
    return (
      <Select
        label={label}
        description={field.description}
        items={items}
        selectedKey={typeof value === 'string' ? value : ''}
        onSelectionChange={(key) => onChange(String(key))}
      />
    );
  }
  if (field.type === 'number' || field.type === 'integer') {
    return (
      <NumberField
        label={label}
        description={field.description}
        value={value === '' ? null : Number(value)}
        step={field.type === 'integer' ? 1 : undefined}
        onChange={(next) => onChange(next === null ? '' : String(next))}
      />
    );
  }
  const Input = field.sensitive ? PasswordField : TextField;
  return <Input label={label} description={field.description} value={typeof value === 'string' ? value : ''} onChange={onChange} />;
}

/** The server's `details.issues` for an invalid-input rejection, as strings. */
function validationIssues(error: Error): string[] {
  if (!(error instanceof ApiError)) return [];
  const issues: unknown = error.details?.issues;
  return Array.isArray(issues) ? issues.filter((issue): issue is string => typeof issue === 'string') : [];
}

function RunForm({
  action,
  personas,
  sandboxId,
  onClose,
}: {
  action: RunnableAction;
  personas: string[];
  sandboxId: string;
  onClose: () => void;
}) {
  const [values, setValues] = React.useState<FormValues>(() => initialFormValues(action.fields, personas));
  const [missing, setMissing] = React.useState<string[]>([]);
  const [invalid, setInvalid] = React.useState<string[]>([]);
  const [runId, setRunId] = React.useState<string | null>(null);
  const run = useRunSandboxV2Action(sandboxId);
  const result = useSandboxV2Run(runId);

  const handleRun = () => {
    const built = buildRunInput(action.fields, values);
    if ('missing' in built) {
      setMissing(built.missing);
      setInvalid(built.invalid);
      return;
    }
    setMissing([]);
    setInvalid([]);
    run.mutate({ action: action.ref, input: built.input }, { onSuccess: (created) => setRunId(created.id) });
  };

  const submitted = runId !== null;
  return (
    <>
      <DialogContent>
        <div className="flex flex-col gap-4">
          {action.fields.length === 0 && !submitted && <Text color="muted">This action takes no input.</Text>}
          {!submitted &&
            action.fields.map((field) => (
              <FieldInput
                key={field.name}
                field={field}
                value={values[field.name] ?? ''}
                personas={personas}
                onChange={(next) => setValues((current) => ({ ...current, [field.name]: next }))}
              />
            ))}
          {missing.length > 0 && (
            <Text size="sm" color="destructive">
              Fill in: {missing.join(', ')}
            </Text>
          )}
          {invalid.length > 0 && (
            <ul className="list-disc pl-5 text-sm text-destructive">
              {invalid.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          )}
          {run.isError && (
            <div role="alert">
              <Text size="sm" color="destructive">
                {run.error.message}
              </Text>
              <ul className="list-disc pl-5 text-sm text-destructive">
                {validationIssues(run.error).map((issue) => (
                  <li key={issue}>{issue}</li>
                ))}
              </ul>
            </div>
          )}
          {submitted &&
            (result.data ? (
              <RunResultView run={result.data} />
            ) : result.isError ? (
              <Text size="sm" color="destructive">
                {result.error.message}
              </Text>
            ) : (
              <LoadingOrbit size="sm" />
            ))}
        </div>
      </DialogContent>
      <DialogFooter>
        <Button variant="outline" onPress={onClose}>
          {submitted ? 'Close' : 'Cancel'}
        </Button>
        {!submitted && (
          <Button variant="primary" onPress={handleRun} isDisabled={run.isPending}>
            {run.isPending ? 'Starting…' : 'Run'}
          </Button>
        )}
      </DialogFooter>
    </>
  );
}

export function RunActionDialog({
  action,
  personas,
  sandboxId,
  onClose,
}: {
  action: RunnableAction | null;
  personas: string[];
  sandboxId: string;
  onClose: () => void;
}) {
  return (
    <DialogTrigger isOpen={action !== null} onOpenChange={(open) => !open && onClose()} size="md">
      <span />
      <Dialog title={action ? `Run ${action.ref}` : 'Run action'} description={action?.description}>
        {action && <RunForm key={action.ref} action={action} personas={personas} sandboxId={sandboxId} onClose={onClose} />}
      </Dialog>
    </DialogTrigger>
  );
}
